import React, { useContext, useEffect, useState } from 'react'
import cn from 'classnames'
import { EntryContentProps } from '@components/renderer'
import { ExternalLink } from '@ui-lib/link'
import { gnarSpec } from '@lib/tracking/gnar'
import { ImageAsset } from '@components/asset/image'
import { PageContext } from '@contexts/page-context'
import { TypeBannerComponent } from '@lib/types/component-banner'
import { getThemeFromBackgroundColor, useBackgroundStyle } from '@components/page-section/theme'
import { useBannerShow } from '@lib/hooks'

import styles from './banner-component.module.scss'
import { getSessionStorageItem, setSessionStorageItem } from '@lib/utils/session-storage'
import { Button } from '@grammarly/badger-ui'
import { CtaButton } from '@components/item/cta-button'
import { IconClose } from '@ui-lib/icons/close'

export const BannerComponent: React.FC<EntryContentProps<TypeBannerComponent>> = ({ structure }) => {
  const { pageId, slug } = useContext(PageContext)
  const { badge, icon, targetUrl, text, cta, background, ctaVariant, bannerTrackingId, showCloseButton, placement } =
    structure.fields
  const [isVisible, setIsVisible] = useState(false)

  const closeHandle = () => {
    setIsVisible(false)
    setSessionStorageItem(bannerTrackingId, 'hidden')
  }

  useEffect(() => {
    // in order to not have a hydration error, initial state is always false, when it renders on cient side it checks session and set needed value
    setIsVisible(showCloseButton ? !getSessionStorageItem(bannerTrackingId) : true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const theme = getThemeFromBackgroundColor(background?.color)

  const backgroundStyles = useBackgroundStyle(theme, background?.color)

  useBannerShow(isVisible, bannerTrackingId, targetUrl)

  if (!isVisible) {
    return null
  }

  return (
    <aside className={cn(styles.banner, placement === 'Bottom' && styles.bannerBottom, ...backgroundStyles)}>
      <ExternalLink
        className={styles.bannerLink}
        target="_blank"
        href={targetUrl}
        onClick={() => {
          gnarSpec.bannerClick(bannerTrackingId, pageId, slug, targetUrl)
        }}
      >
        {icon && (
          <span className={styles.bannerIcon}>
            <ImageAsset structure={icon} />
          </span>
        )}
        {badge && <span className={cn(styles.badge, theme === 'light' && styles.badgeLight)}>{badge}</span>}
        {text}
        <div className={styles.buttonContainer}>
          <CtaButton
            className={cn(styles.button, {
              [styles.buttonAsText]: ctaVariant === 'Text',
              [styles.buttonCompact]: ctaVariant === 'Compact',
            })}
            structure={cta}
          />
        </div>
      </ExternalLink>
      {showCloseButton && (
        <Button className={styles.closeButton} onClick={closeHandle} padding="compact">
          <IconClose color="neutral40" />
        </Button>
      )}
    </aside>
  )
}
